import { forwardRef } from '@chakra-ui/react';
import { memo, useMemo } from 'react';

import { Box, type BoxProps } from '@frontend/components';

import { useTableStyles } from '../TableStylesContext';
import { useTableContext } from '../utils/context';

const RELAXED_PADDING_BY_SIZE: Record<string, number> = {
  sm: 2.25,
  md: 2.5,
};

export type TableHeaderProps = BoxProps;

/**
 * A Table subcomponent used to contain elements above the Table's grid.
 * - Place within the Table before the TableContent and TableFooter.
 * - Wraps a Chakra-UI Box and responds to Table's size.
 */
export const TableHeader = memo(
  forwardRef<TableHeaderProps, 'div'>(({ children, ...restProps }, ref) => {
    const { header: headerStyles } = useTableStyles() ?? {};

    const { density, size } = useTableContext() ?? {};

    const headerCss = useMemo(
      () => ({
        ...headerStyles,
        ...(density === 'relaxed'
          ? {
              px: RELAXED_PADDING_BY_SIZE[size],
            }
          : null),
      }),
      [headerStyles, density, size],
    );

    return (
      <Box ref={ref} __css={headerCss} {...restProps}>
        {children}
      </Box>
    );
  }),
);
TableHeader.displayName = 'MemoizedTableHeader';
